import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../hooks/redux';
import { FormInputText } from '../../../inputs/FormInputText';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { ButtonClassNames, ModalVariants, InputTextFormat, InputRegexPattern } from '../../../../models/Common';
import { ITerminationStep, ITerminateForm } from '../../../../models/ContractTermination';
import { formatDateAsISO, getErrorMessage } from '../../../../utils';

interface TerminateTransferProps {
  formState: ITerminateForm;
  changeTerminateStep: (step: ITerminationStep) => void;
}

interface TerminateTransferFormState {
  contractNumber: string;
}

export const TerminateTransfer: FC<TerminateTransferProps> = ({ formState, changeTerminateStep }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [createUserDocument, { isLoading }] = userInfoApi.useCreateUserDocumentMutation();
  const methods = useForm<TerminateTransferFormState>({
    defaultValues: {
      contractNumber: '',
    },
  });
  const { handleSubmit } = methods;

  const handleProceed = async (data: TerminateTransferFormState) => {
    try {
      const toDate = formatDateAsISO(
        Number(formState.terminationDate?.day),
        Number(formState.terminationDate?.month),
        Number(formState.terminationDate?.year),
      );
      const terminationPhone = `+38${formState.phone}`;
      const body = {
        toDate,
        terminationPhone,
        refundDetails: {
          refundId: data.contractNumber,
        },
      };
      await createUserDocument({ docType: 'balanceTransfer', body }).unwrap();
      dispatch(
        openModal({
          modalVariants: ModalVariants.Success,
          content: {
            title: `${t('terminateContract.applicationSent')}`,
            description: `${t('terminateContract.supportManagerWillContactYou')}`,
          },
        }),
      );
      changeTerminateStep(undefined);
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  return (
    <FormProvider {...methods}>
      <article className="account__terminate__title">
        <span>{t('terminateContract.fillContractNumber')}</span>
      </article>
      <form className="account__terminate__transfer" onSubmit={handleSubmit(handleProceed)}>
        <div className="account__terminate__transfer__inputs">
          <div className="account__terminate__transfer__input">
            <FormInputText
              name="contractNumber"
              label={t('terminateContract.accountNumber')}
              className="primary"
              isRequired
              minLength={4}
              format={InputTextFormat.ContractNumber}
              pattern={InputRegexPattern.OnlyNumbers}
            />
          </div>
        </div>
        <div className="modals__btns-wrapper">
          <div className="btn-group">
            <CustomMUIButton
              label={t('common.back')}
              type="button"
              className={ButtonClassNames.PrimaryFulfilled}
              handleClick={() => changeTerminateStep('refund')}
              isBold
            />
          </div>
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('terminateContract.sendApplication')}
              type="submit"
              className={ButtonClassNames.SecondaryOutlined}
              isDisabled={isLoading}
              isBold
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
